import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { initCounterBlock } from "./counter";
import { attachScrollTriggerOrPlayNow } from "../utils/scrollTriggerHelpers";

gsap.registerPlugin(ScrollTrigger);

/**
 * Percorso block animation.
 *
 * Desktop (>= 992px):
 *  - Pin del wrapper interno durante la timeline
 *  - Timeline orchestrata con scrub: true (controllata dallo scroll)
 *  - Sequenza percentuale della timeline:
 *      0%   → percorso 0%, tutto altro invisibile
 *      0-50%   → percorso si forma (stroke-dashoffset)
 *      35-55%  → immagine appare (fade + scale + blur)
 *      55-70%  → titolo
 *      65-80%  → testo
 *      75-90%  → CTA
 *      100%    → tutto visibile
 *
 * Mobile (< 992px):
 *  - No pin, no scrub
 *  - Animazione one-shot quando il blocco entra in viewport
 *
 * Stroke animation: manuale via getTotalLength() + stroke-dashoffset.
 * Non uso il plugin DrawSVG per evitare dipendenze extra.
 */

const initPercorsoBlock = (block) => {
  const path = block.querySelector("[data-percorso-path]");
  const image = block.querySelector("[data-percorso-image]");
  const title = block.querySelector("[data-percorso-title]");
  const text = block.querySelector("[data-percorso-text]");
  const cta = block.querySelector("[data-percorso-cta]");
  const pinWrapper = block.querySelector(".percorso__pin-wrapper");

  if (!path || !pinWrapper) return;

  // Counter opzionale interno: inizializza con la stessa logica del blocco
  // counter standalone (ScrollTrigger indipendente, one-shot all'entrata)
  const counterSection = block.querySelector("[data-percorso-counters]");
  if (counterSection) {
    initCounterBlock(counterSection);
  }

  // Calcolo lunghezza totale path per stroke animation
  const pathLength = path.getTotalLength();
  gsap.set(path, {
    strokeDasharray: pathLength,
    strokeDashoffset: pathLength,
  });

  // Stato iniziale elementi: immagine ha scale + blur, gli altri solo opacity
  gsap.set(image, { opacity: 0, scale: 1.1, filter: "blur(12px)" });
  gsap.set([title, text, cta].filter(Boolean), { opacity: 0, y: 16, filter: "blur(8px)" });

  // ScrollTrigger.matchMedia: comportamento diverso per breakpoint
  ScrollTrigger.matchMedia({
    // ============================================================================
    // DESKTOP (>= 992px): pin + scrub
    // ============================================================================
    "(min-width: 992px)": () => {
      // Leggo dinamicamente l'altezza del header sticky (può variare in is-scrolled)
      const header = document.querySelector(".header");
      const headerOffset = header ? header.offsetHeight : 80;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: block, // intera section: il pin scatta quando il counter arriva sotto al header
          start: `top top+=${headerOffset}`, // counter si "appoggia" al bordo inferiore del header
          end: "+=150%", // l'utente scrolla ~1.5x viewport prima che la timeline finisca
          scrub: 0.5, // smoothing 0.5s per uno scrub più morbido (anti-scatti)
          pin: block, // pin dell'intera section: counter resta visibile insieme al percorso durante l'animazione
          pinSpacing: true,
          anticipatePin: 1,
          onLeave: () => block.setAttribute("data-percorso-ready", "true"),
        },
      });

      // Percorso si forma (0% → 50% della timeline)
      tl.to(path, {
        strokeDashoffset: 0,
        ease: "none",
      }, 0);

      // Immagine appare (35% → 55%)
      tl.to(image, {
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        ease: "power2.out",
      }, 0.35);

      // Titolo (55% → 70%)
      tl.to(title, {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        ease: "power2.out",
      }, 0.55);

      // Testo (65% → 80%)
      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          ease: "power2.out",
        }, 0.65);
      }

      // CTA (75% → 90%)
      if (cta) {
        tl.to(cta, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          ease: "power2.out",
        }, 0.75);
      }
    },

    // ============================================================================
    // MOBILE (< 992px): one-shot, no pin/scrub
    // ============================================================================
    "(max-width: 991.98px)": () => {
      const tl = gsap.timeline({ paused: true });

      // Percorso si forma (1.5s)
      tl.to(path, {
        strokeDashoffset: 0,
        duration: 1.5,
        ease: "power2.out",
      }, 0);

      // Immagine
      tl.to(image, {
        opacity: 1,
        scale: 1,
        filter: "blur(0px)",
        duration: 1.0,
        ease: "power2.out",
      }, 0.6);

      // Titolo
      tl.to(title, {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.8,
        ease: "power2.out",
      }, 1.0);

      // Testo
      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.8,
          ease: "power2.out",
        }, 1.2);
      }

      // CTA
      if (cta) {
        tl.to(cta, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.8,
          ease: "power2.out",
        }, 1.4);
      }

      // Attach ScrollTrigger o play immediato se già in viewport
      attachScrollTriggerOrPlayNow(tl, {
        trigger: block,
        start: "top 75%",
        onComplete: () => block.setAttribute("data-percorso-ready", "true"),
      });
    },
  });
};

const initAllPercorso = () => {
  const blocks = document.querySelectorAll("[data-percorso-block]");
  blocks.forEach(initPercorsoBlock);
};

export default () => {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllPercorso);
  } else {
    initAllPercorso();
  }
};